// Physical reads for the catalog-owned equipment history and admin recovery
// resources. The coordinator registers these exact functions in the catalog;
// presenters never issue render reads or choose an authenticated identity.
import type {SupabaseClient} from '../../lib/supabaseTypes.js';
import type {OwnerFuelingRow, OwnerFuelSupplyRow, OwnerReportRow} from '../../lib/ownerReportsApi.js';

export const OWNER_EQUIPMENT_REPORTS_RESOURCE = 'equipment.ownReports';
export const OWNER_REPORT_RECOVERY_RESOURCE = 'ownerReports.deleted';
export type OwnerEquipmentReport =
  | {kind: 'fueling'; row: OwnerFuelingRow; equipmentName: string; unit: 'km' | 'hours'}
  | {kind: 'supply'; row: OwnerFuelSupplyRow};
export type OwnerEquipmentReportsData = {reports: OwnerEquipmentReport[]};
export type OwnerRecoveryRow = {kind: 'fueling' | 'supply' | 'cattle-log'; row: OwnerReportRow};
export type OwnerReportRecoveryData = {reports: OwnerRecoveryRow[]};

function rows(data: unknown, label: string): OwnerReportRow[] {
  if (!Array.isArray(data) || data.some((row) => !row || typeof row.id !== 'string' || !row.id)) {
    throw new Error('Could not read ' + label + '. Retry the list.');
  }
  return data;
}

export async function queryOwnerEquipmentReports(
  sb: SupabaseClient,
  params?: {profileId?: unknown},
  signal?: AbortSignal,
): Promise<OwnerEquipmentReportsData> {
  const profileId = typeof params?.profileId === 'string' ? params.profileId : '';
  if (!profileId) throw new Error('A signed-in report owner is required.');
  let fuelingQuery = sb
    .from('equipment_fuelings')
    .select(
      'id,equipment_id,date,gallons,fuel_type,hours_reading,km_reading,def_gallons,comments,team_member,submitted_at,owner_profile_id,photos,deleted_at',
    )
    .eq('owner_profile_id', profileId)
    .is('deleted_at', null)
    .order('date', {ascending: false})
    .limit(500);
  let supplyQuery = sb
    .from('fuel_supplies')
    .select(
      'id,date,gallons,fuel_type,destination,notes,supplier,cost_per_gal,total_cost,team_member,created_at,owner_profile_id,deleted_at',
    )
    .eq('owner_profile_id', profileId)
    .is('deleted_at', null)
    .order('date', {ascending: false})
    .limit(500);
  if (signal) {
    fuelingQuery = fuelingQuery.abortSignal(signal);
    supplyQuery = supplyQuery.abortSignal(signal);
  }
  const [fueling, supply] = await Promise.all([fuelingQuery, supplyQuery]);
  if (fueling.error) throw new Error('Fueling reports: ' + fueling.error.message);
  if (supply.error) throw new Error('Fuel supply reports: ' + supply.error.message);
  const fuelings = rows(fueling.data, 'fueling reports').filter(
    (row) => row.owner_profile_id === profileId && !row.deleted_at,
  ) as OwnerFuelingRow[];
  const supplies = rows(supply.data, 'fuel supply reports').filter(
    (row) => row.owner_profile_id === profileId && !row.deleted_at,
  ) as OwnerFuelSupplyRow[];
  const ids = [...new Set(fuelings.map((row) => row.equipment_id).filter(Boolean))];
  const names = new Map<string, {name: string; unit: 'km' | 'hours'}>();
  if (ids.length) {
    let request = sb.from('equipment').select('id,name,tracking_unit').in('id', ids);
    if (signal) request = request.abortSignal(signal);
    const equipment = await request;
    if (equipment.error) throw new Error('Equipment names: ' + equipment.error.message);
    for (const row of rows(equipment.data, 'equipment names'))
      names.set(row.id, {name: String(row.name || row.id), unit: row.tracking_unit === 'km' ? 'km' : 'hours'});
  }
  const reports: OwnerEquipmentReport[] = [
    ...fuelings.map((row) => ({
      kind: 'fueling' as const,
      row,
      equipmentName: names.get(row.equipment_id)?.name || row.equipment_id,
      unit: names.get(row.equipment_id)?.unit || ('hours' as const),
    })),
    ...supplies.map((row) => ({kind: 'supply' as const, row})),
  ];
  reports.sort((a, b) => (b.row.date || '').localeCompare(a.row.date || '') || b.row.id.localeCompare(a.row.id));
  return {reports};
}

export async function queryOwnerReportRecovery(
  sb: SupabaseClient,
  _params?: unknown,
  signal?: AbortSignal,
): Promise<OwnerReportRecoveryData> {
  let fueling = sb.rpc('list_deleted_equipment_fuelings', {p_limit: 500});
  let supply = sb.rpc('list_deleted_fuel_supplies', {p_limit: 500});
  let log = sb.rpc('list_deleted_cattle_log_entries', {p_limit: 500});
  if (signal) {
    fueling = fueling.abortSignal(signal);
    supply = supply.abortSignal(signal);
    log = log.abortSignal(signal);
  }
  const replies = await Promise.all([fueling, supply, log]);
  const kinds = ['fueling', 'supply', 'cattle-log'] as const;
  const reports: OwnerRecoveryRow[] = [];
  replies.forEach((result, index) => {
    if (result.error) throw new Error('Deleted ' + kinds[index] + ' reports: ' + result.error.message);
    for (const row of rows(result.data, 'deleted ' + kinds[index] + ' reports'))
      reports.push({kind: kinds[index], row});
  });
  reports.sort(
    (a, b) => (b.row.deleted_at || '').localeCompare(a.row.deleted_at || '') || b.row.id.localeCompare(a.row.id),
  );
  return {reports};
}
